/**
 * The comparison chart: one timeline, the baseline drawn beneath today.
 *
 * NOT a `GanttView`. Two of those side by side was the original design and it
 * failed on its own terms — each carried its own label column repeating the
 * same titles, its own axis, its own scrollbar and its own grouping, so an
 * issue whose status changed sat in a different band on each half and row N
 * stopped meaning the same issue on both.
 *
 * One row per issue instead, split into two lanes: the snapshot's bar on top,
 * today's directly beneath it. Neither ever occludes the other, and the
 * horizontal offset between the two IS the delta — which is the whole thing
 * the reader came for. An issue missing on one side leaves that lane empty,
 * so the top lane always means "then" and the bottom one "now".
 *
 * It needs none of `GanttView`'s drag, link gesture, grouping, context menu,
 * staging, unscheduled band or manual row order — a comparison is a reading
 * surface. It shares the axis maths, and the axis header itself.
 */
import { useMemo, useRef, useState, type CSSProperties } from "react";

import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { SnapshotDiffEntry } from "@/domains/pm/utils/scheduleSnapshotDiff";
import {
  barRect,
  buildScale,
  daysToFill,
  fitDateRange,
  scaleWidth,
  todayX,
  type GanttGranularity,
} from "@/domains/pm/utils/ganttScale";
import { GanttHeader } from "@/domains/pm/components/issues/gantt/GanttHeader";
import {
  GANTT_BAR_HEIGHT,
  GANTT_LABEL_WIDTH,
} from "@/domains/pm/components/issues/gantt/ganttConstants";
import { useGanttViewport } from "@/domains/pm/components/issues/gantt/useGanttViewport";
import { CHANGE_TONE, COMPARE_BAR } from "./snapshotDiffTones";

/** One lane — a bar plus breathing room. Two of them make an issue's row, so
 *  the pair stays close to a live-chart row's height rather than doubling it. */
const COMPARE_LANE_HEIGHT = 24;

export function SnapshotCompareTimeline({
  rows,
  granularity,
  selectedIssueId,
  onSelectIssue,
}: {
  /** Already filtered and ordered by the dialog — the chart draws what it is given. */
  rows: SnapshotDiffEntry[];
  granularity: GanttGranularity;
  selectedIssueId?: number;
  onSelectIssue?: (issueId: number) => void;
}) {
  const { t } = useLanguage();
  const copy = t.pm.issues.gantt;
  const scrollRef = useRef<HTMLDivElement>(null);
  // The chart fills the dialog, the same way the live one fills the page.
  const viewportWidth = useGanttViewport(scrollRef);

  // The union of BOTH schedules. Taking only today's would push a snapshot bar
  // off the axis whenever work moved earlier. `fitDateRange`, not the live
  // chart's padded range: when every bar fits, there is no sideways scroll.
  const scale = useMemo(() => {
    const dates: string[] = [];
    for (const r of rows) {
      for (const d of [r.snapshotStart, r.snapshotDue, r.currentStart, r.currentDue]) {
        if (d) dates.push(d);
      }
    }
    const minDays = daysToFill(viewportWidth - GANTT_LABEL_WIDTH, granularity);
    return buildScale(fitDateRange(dates, new Date(), minDays), granularity);
  }, [rows, granularity, viewportWidth]);

  const width = scaleWidth(scale);
  const marker = todayX(scale);
  // A fitted range can leave today off the axis; a marker placed there anyway
  // would widen the scroll area and bring back the scrollbar it just removed.
  const markerOnAxis = marker >= 0 && marker < width;

  return (
    <div
      ref={scrollRef}
      className="gantt-compare-scroll relative flex-1 overflow-auto border-t"
      // The horizontal scrollbar starts where the timeline does, not under
      // the sticky title column — see .gantt-compare-scroll in index.css.
      style={{ "--gantt-scroll-inset": `${GANTT_LABEL_WIDTH}px` } as CSSProperties}
    >
      {/* One column at `min-h-full` — see the note in GanttView. */}
      <div className="flex min-h-full flex-col" style={{ width: GANTT_LABEL_WIDTH + width }}>
        <GanttHeader scale={scale} labelWidth={GANTT_LABEL_WIDTH} />

        <div className="relative flex flex-1 flex-col">
          {/* Weekends and the today line. Decoration only. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 z-0"
            style={{ left: GANTT_LABEL_WIDTH, width }}
          >
            {markerOnAxis && (
              <div className="absolute inset-y-0 w-0.5 bg-[#ef4129]" style={{ left: marker }} />
            )}
          </div>

          <div className="relative z-10 shrink-0">
            {rows.map((row) => (
              <CompareRow
                key={row.issue_id}
                row={row}
                scale={scale}
                width={width}
                selected={selectedIssueId === row.issue_id}
                onSelect={onSelectIssue}
              />
            ))}
          </div>

          {/* The grid carries on below the last row — see the note in GanttView. */}
          <div className="relative flex min-h-0 flex-1">
            {rows.length === 0 ? (
              <p className="sticky left-0 px-7 py-8 text-xs text-muted-foreground">
                {copy.compareNoRows}
              </p>
            ) : (
              <div
                aria-hidden="true"
                className="sticky left-0 shrink-0 border-r bg-background"
                style={{ width: GANTT_LABEL_WIDTH }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function CompareRow({
  row,
  scale,
  width,
  selected,
  onSelect,
}: {
  row: SnapshotDiffEntry;
  scale: ReturnType<typeof buildScale>;
  width: number;
  selected: boolean;
  onSelect?: (issueId: number) => void;
}) {
  const { t } = useLanguage();
  const copy = t.pm.issues.gantt;
  const tone = CHANGE_TONE[row.kind];
  const [expanded, setExpanded] = useState(false);

  const live = row.currentStart || row.currentDue;
  const base = row.snapshotStart || row.snapshotDue;
  const liveRect = live ? barRect(scale, row.currentStart ?? live, row.currentDue ?? live) : null;
  const baseRect = base ? barRect(scale, row.snapshotStart ?? base, row.snapshotDue ?? base) : null;

  /** Both bars are the same height and sit on their lane's centre line: the
   *  lane says which is which, and the fill repeats it for the legend. */
  const bar = (rect: { left: number; width: number }, background: string, title: string) => (
    <div
      className="absolute top-1/2 -translate-y-1/2 rounded-[3px]"
      style={{ left: rect.left, width: rect.width, height: GANTT_BAR_HEIGHT, background }}
      title={title}
    />
  );

  return (
    <div
      data-compare-row={row.issue_id}
      // A darker rule between issues than the faint dashed one
      // between an issue's own lanes: the contrast is what groups each pair.
      className="flex border-b border-foreground/40"
      // minHeight, not height: an expanded title wraps and the row grows with
      // it, while the two lanes stay together on the row's centre line.
      style={{ minHeight: 2 * COMPARE_LANE_HEIGHT }}
    >
      <button
        type="button"
        onClick={() => {
          setExpanded((v) => !v);
          onSelect?.(row.issue_id);
        }}
        aria-expanded={expanded}
        title={row.title}
        className={cn(
          // The same left rule the live chart uses, and opaque at every
          // state for the same reason -- see the note in `GanttRow`.
          // items-center: the title sits midway between its two lanes.
          "sticky left-0 z-10 flex shrink-0 items-center gap-1.5 border-l-2 border-r bg-background px-3 py-1 text-left text-xs",
          selected ? "border-l-[#ef4129]" : "border-l-transparent hover:border-l-[#ef4129]/40"
        )}
        style={{ width: GANTT_LABEL_WIDTH }}
      >
        <span className={cn("h-3 w-0.5 shrink-0 rounded-full", tone.bar)} aria-hidden="true" />
        <span className="shrink-0 text-[10px] text-muted-foreground">#{row.issue_number}</span>
        {/* A truncated title is the commonest reason to squint at this chart,
            and the column is not wide enough to fix by widening. */}
        <span
          className={cn(
            expanded ? "break-words" : "truncate",
            row.kind === "removed" && "text-muted-foreground"
          )}
        >
          {row.title}
        </span>
      </button>

      <div className="flex flex-col justify-center" style={{ width }}>
        {/* The faint rule pairs the two lanes as one issue; the row's own
            darker solid border is what separates issues. */}
        <div
          data-compare-lane="baseline"
          className="relative border-b border-dashed border-border/40"
          style={{ height: COMPARE_LANE_HEIGHT }}
        >
          {baseRect && bar(baseRect, COMPARE_BAR.baseline, copy.compareBaselineBar)}
        </div>
        <div data-compare-lane="today" className="relative" style={{ height: COMPARE_LANE_HEIGHT }}>
          {liveRect && bar(liveRect, COMPARE_BAR.today, copy.compareLegendToday)}

          {/* The delta, OUTSIDE the bar. Inside it, a one-day bar at week
              granularity is 12px wide and clips the label away entirely. */}
          {liveRect && row.deltaDays != null && row.deltaDays !== 0 && (
            <span
              className={cn(
                "absolute top-1/2 -translate-y-1/2 whitespace-nowrap pl-1.5 text-[10px] font-medium tabular-nums",
                row.deltaDays > 0 ? "text-amber-600" : "text-emerald-600"
              )}
              style={{ left: liveRect.left + liveRect.width }}
            >
              {row.deltaDays > 0 ? `+${row.deltaDays}d` : `${row.deltaDays}d`}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
